// LLM & Reports v3.2 §13 — typed wrappers over the new backend endpoints
// PR 1 + PR 2 added: per-submission document attachments, document tag
// lookup, prompt preview / token budget, two-stage rendering (apply
// branding / mark finalized / revert), and the BrandSettings exemplar
// upload lifecycle.
//
// The legacy v3.1 reportsApi (./reports.ts) is preserved verbatim —
// pre-v3.2 code paths still reach for it and shouldn't be disturbed.

import { api } from "./client";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

// ---------------------------------------------------------------------------
// Stage + lifecycle types
// ---------------------------------------------------------------------------

export type ReportGenerationStage = "Drafted" | "BrandingApplied" | "Finalized";

export type DocumentConversionStatus =
  | "Pending"
  | "Succeeded"
  | "Failed"
  | "Skipped";

// Mirrors PhoenixCapital.Core.Interfaces.Reports.BrandedRenderingResult.
export interface BrandedRenderingResult {
  reportId: string;
  docxBlobPath: string;
  pdfBlobPath: string;
  exemplarVersionUsed: string;
  exemplarBlobPathUsed: string | null;
}

// ---------------------------------------------------------------------------
// Document tags
// ---------------------------------------------------------------------------

export interface DocumentTagDto {
  id: string;
  tagCode: string;
  displayName: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystemReserved: boolean;
  createdAt: string;
  modifiedAt: string;
}

export const documentTagsApi = {
  list: () => api.get<DocumentTagDto[]>("/document-tags"),
};

// ---------------------------------------------------------------------------
// Per-submission documents (attach / list / detach / re-tag / retry)
// ---------------------------------------------------------------------------

export interface ReportSubmissionDocumentDto {
  id: string;
  reportSubmissionId: string;
  documentId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  tagCode: string | null;
  displayOrder: number;
  sourceFileHashSha256: string;
  convertedArtifactBlobPath: string | null;
  convertedArtifactHashSha256: string | null;
  conversionStatus: DocumentConversionStatus;
  conversionError: string | null;
  estimatedInputTokens: number;
  createdAt: string;
}

export interface UpdateReportSubmissionDocumentPayload {
  tagCode?: string | null;
  displayOrder?: number | null;
}

export interface AttachSubmissionDocumentPayload {
  file: File;
  folderId: string;
  tagCode?: string | null;
  displayOrder?: number | null;
}

export const submissionDocumentsApi = {
  list: (submissionId: string) =>
    api.get<ReportSubmissionDocumentDto[]>(
      `/reports/submissions/${submissionId}/documents`,
    ),

  attach: (submissionId: string, payload: AttachSubmissionDocumentPayload) => {
    const form = new FormData();
    form.append("file", payload.file);
    form.append("folderId", payload.folderId);
    if (payload.tagCode) form.append("tagCode", payload.tagCode);
    if (payload.displayOrder !== undefined && payload.displayOrder !== null) {
      form.append("displayOrder", String(payload.displayOrder));
    }
    return api.upload<ReportSubmissionDocumentDto>(
      `/reports/submissions/${submissionId}/documents`,
      form,
    );
  },

  detach: (submissionId: string, joinRowId: string) =>
    api.delete<void>(`/reports/submissions/${submissionId}/documents/${joinRowId}`),

  update: (
    submissionId: string,
    joinRowId: string,
    payload: UpdateReportSubmissionDocumentPayload,
  ) =>
    api.patch<ReportSubmissionDocumentDto>(
      `/reports/submissions/${submissionId}/documents/${joinRowId}`,
      payload,
    ),

  retry: (submissionId: string, joinRowId: string) =>
    api.post<ReportSubmissionDocumentDto>(
      `/reports/submissions/${submissionId}/documents/${joinRowId}/retry`,
    ),

  // v3.6 — attach existing repository documents by reference (no
  // re-upload). The backend runs reports ingestion for each so generation
  // gets a valid conversion record; idempotent on re-attach.
  attachByReference: (
    submissionId: string,
    documentIds: string[],
    tagCode?: string | null,
  ) =>
    api.post<ReportSubmissionDocumentDto[]>(
      `/reports/submissions/${submissionId}/documents/attach`,
      { documentIds, tagCode: tagCode ?? null },
    ),
};

/**
 * v3.6 — readiness gate for Generate. A report can be generated once every
 * attached document has reached a terminal, non-blocking conversion state:
 * <c>Succeeded</c> (consumed) or <c>Skipped</c> (unsupported format, an
 * explicit backend determination that's safe to proceed without). A row
 * still <c>Pending</c> or <c>Failed</c> blocks generation until it converts
 * or is retried/removed — this is the fix for the "has no ingestion record"
 * failure class (§6.8).
 */
export function allAttachmentsReady(
  rows: Pick<ReportSubmissionDocumentDto, "conversionStatus">[],
): boolean {
  return rows.every(
    (r) =>
      r.conversionStatus === "Succeeded" || r.conversionStatus === "Skipped",
  );
}

// ---------------------------------------------------------------------------
// Prompt preview + token budget
// ---------------------------------------------------------------------------

export type PreviewContentBlockKind = "text" | "document" | "image";

export interface PreviewContentBlockDto {
  kind: PreviewContentBlockKind;
  title: string;
  estimatedTokens: number;
  mediaType: string | null;
}

export interface DocumentTokenBreakdownDto {
  documentId: string;
  fileName: string;
  tagCode: string | null;
  estimatedTokens: number;
  conversionStatus: string;
}

export interface TokenBudgetEstimateDto {
  promptTokens: number;
  formResponseTokens: number;
  brandingGuideTokens: number;
  documentTokens: number;
  totalTokens: number;
  thresholdLimit: number;
  exceedsThreshold: boolean;
  perDocument: DocumentTokenBreakdownDto[];
}

export interface PromptPreviewDto {
  promptBody: string;
  formResponses: string;
  brandingGuide: string;
  contentBlocks: PreviewContentBlockDto[];
  tokenBudget: TokenBudgetEstimateDto;
}

export const promptPreviewApi = {
  preview: (submissionId: string) =>
    api.get<PromptPreviewDto>(
      `/reports/submissions/${submissionId}/preview-prompt`,
    ),
};

// ---------------------------------------------------------------------------
// Two-stage rendering (apply branding / mark finalized / revert)
// ---------------------------------------------------------------------------

export const reportRenderingApi = {
  applyBranding: (reportId: string) =>
    api.post<BrandedRenderingResult>(`/reports/${reportId}/apply-branding`),

  markFinalized: (reportId: string) =>
    api.post<BrandedRenderingResult>(`/reports/${reportId}/mark-finalized`),

  revertFinalization: (reportId: string, reason: string) =>
    api.post<BrandedRenderingResult>(
      `/reports/${reportId}/revert-finalization`,
      { reason },
    ),
};

// ---------------------------------------------------------------------------
// Markdown blob fetch — the v3.2 Drafted artifact lives in Blob; the
// browser hits a backend proxy that streams the bytes back. Returns
// raw markdown text. Falls back to empty string when the report has
// no MarkdownBlobPath (legacy v3.1 rows).
// ---------------------------------------------------------------------------

export const reportMarkdownApi = {
  get: async (reportId: string): Promise<string> => {
    const response = await fetch(`${API_BASE}/reports/${reportId}/markdown`, {
      credentials: "include",
      headers: { Accept: "text/markdown, text/plain" },
    });
    if (!response.ok) {
      throw new Error(`Failed to load report markdown (${response.status})`);
    }
    return response.text();
  },
};

// ---------------------------------------------------------------------------
// Brand exemplar — Section 6 of the Brand Identity admin page
// ---------------------------------------------------------------------------

export interface ExemplarVersionDto {
  blobPath: string;
  fileName: string;
  versionLabel: string;
  uploadedDate: string;
  uploadedById: string;
  uploadedByDisplayName: string | null;
  isActive: boolean;
}

export const brandExemplarApi = {
  upload: (file: File, versionLabel: string) => {
    const form = new FormData();
    form.append("file", file);
    form.append("versionLabel", versionLabel);
    return api.upload<unknown>("/admin/brand-settings/exemplar", form);
  },

  revertToDefault: () =>
    api.delete<unknown>("/admin/brand-settings/exemplar"),

  history: (limit = 10) =>
    api.get<ExemplarVersionDto[]>("/admin/brand-settings/exemplar/history", {
      limit,
    }),

  activate: (blobPath: string) =>
    api.post<unknown>("/admin/brand-settings/exemplar/activate", { blobPath }),

  download: async (): Promise<Blob> => {
    const response = await fetch(
      `${API_BASE}/admin/brand-settings/exemplar/download`,
      { credentials: "include" },
    );
    if (!response.ok) {
      throw new Error(`Failed to download exemplar (${response.status})`);
    }
    return response.blob();
  },
};
